import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ClipboardList,
  Users,
  FileText,
  Calendar,
  Settings,
  BarChart3,
  Package,
  Wrench,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useUIStore } from '@/stores/uiStore';
import api from '@/services/api';

interface SearchResult {
  id: string;
  type: 'work_order' | 'customer' | 'action';
  label: string;
  description?: string;
  href: string;
  icon: typeof Search;
}

const quickActions: SearchResult[] = [
  { id: 'new-order', type: 'action', label: 'Nueva orden de trabajo', href: '/work-orders/new', icon: ClipboardList },
  { id: 'go-orders', type: 'action', label: 'Ordenes de trabajo', href: '/work-orders', icon: ClipboardList },
  { id: 'go-customers', type: 'action', label: 'Clientes', href: '/customers', icon: Users },
  { id: 'go-calendar', type: 'action', label: 'Calendario', href: '/calendar', icon: Calendar },
  { id: 'go-invoices', type: 'action', label: 'Facturas', href: '/invoices', icon: FileText },
  { id: 'go-estimates', type: 'action', label: 'Presupuestos', href: '/estimates', icon: FileText },
  { id: 'go-inventory', type: 'action', label: 'Inventario', href: '/inventory', icon: Package },
  { id: 'go-team', type: 'action', label: 'Equipo', href: '/team', icon: Users },
  { id: 'go-reports', type: 'action', label: 'Informes', href: '/reports', icon: BarChart3 },
  { id: 'go-settings', type: 'action', label: 'Ajustes', href: '/settings', icon: Settings },
];

function CommandPalette() {
  const navigate = useNavigate();
  const open = useUIStore((s) => s.commandPaletteOpen);
  const setOpen = useUIStore((s) => s.setCommandPaletteOpen);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Search API when query changes
  const searchApi = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const [woRes, custRes] = await Promise.allSettled([
        api.get('/work-orders', { params: { search: q, limit: 5 } }),
        api.get('/customers', { params: { search: q, limit: 5 } }),
      ]);

      const items: SearchResult[] = [];

      if (woRes.status === 'fulfilled') {
        const orders = woRes.value.data?.data ?? [];
        for (const wo of orders) {
          items.push({
            id: `wo-${wo.id}`,
            type: 'work_order',
            label: `#${wo.number} ${wo.title}`,
            description: wo.customerName ?? undefined,
            href: `/work-orders/${wo.id}`,
            icon: ClipboardList,
          });
        }
      }

      if (custRes.status === 'fulfilled') {
        const customers = custRes.value.data?.data ?? [];
        for (const c of customers) {
          items.push({
            id: `cust-${c.id}`,
            type: 'customer',
            label: c.name,
            description: c.company ?? c.email ?? undefined,
            href: `/customers/${c.id}`,
            icon: Users,
          });
        }
      }

      setResults(items);
      setSelectedIndex(0);
    } catch {
      // Silently fail — search is best-effort
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => searchApi(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query, searchApi]);

  // Display items: if searching, show API results; otherwise filtered quick actions
  const displayItems = query.trim().length >= 2
    ? results
    : quickActions.filter((a) =>
        a.label.toLowerCase().includes(query.toLowerCase())
      );

  // Keyboard navigation
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, displayItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && displayItems[selectedIndex]) {
      e.preventDefault();
      selectItem(displayItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function selectItem(item: SearchResult) {
    setOpen(false);
    navigate(item.href);
  }

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Palette */}
      <div className="relative w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 dark:border-slate-700">
          <Search className="h-5 w-5 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar ordenes, clientes, acciones..."
            className="h-12 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
          />
          {isSearching && (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500" />
          )}
          <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 dark:border-slate-600 dark:bg-slate-800">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-72 overflow-y-auto py-2">
          {displayItems.length === 0 && query.trim().length >= 2 && !isSearching && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">
              Sin resultados para "{query}"
            </p>
          )}

          {displayItems.length === 0 && query.trim().length < 2 && (
            <p className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-400">
              Acciones rapidas
            </p>
          )}

          {query.trim().length < 2 && displayItems.length > 0 && (
            <>
              <p className="px-4 pb-1 text-xs font-medium uppercase tracking-wider text-slate-400">
                Ir a
              </p>
            </>
          )}

          {query.trim().length >= 2 && results.length > 0 && (
            <p className="px-4 pb-1 text-xs font-medium uppercase tracking-wider text-slate-400">
              Resultados
            </p>
          )}

          {displayItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                data-index={idx}
                onClick={() => selectItem(item)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                  idx === selectedIndex
                    ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400'
                    : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                )}
              >
                <Icon className="h-4 w-4 shrink-0 opacity-60" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">
                    {item.label}
                  </span>
                  {item.description && (
                    <span className="text-xs text-slate-400 truncate block">
                      {item.description}
                    </span>
                  )}
                </div>
                {idx === selectedIndex && (
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-40" />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2 dark:border-slate-700">
          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] dark:border-slate-600 dark:bg-slate-800">↑↓</kbd>
              Navegar
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] dark:border-slate-600 dark:bg-slate-800">↵</kbd>
              Seleccionar
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Wrench className="h-3 w-3 text-slate-300" />
            <span className="text-[11px] text-slate-300">WorSaaS</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
